<template>
    <div class="cpe" :class="{ 'cpe--focused': focused }">
        <editor-content :editor="editor" class="cpe__content" />
    </div>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-2";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import CoPilotAttrExtension from "./CoPilotAttrExtension";
import { getAttributeShortLabel } from "./copilotAttributes";

export default {
    name: "CoPilotPromptEditor",
    components: { EditorContent },
    props: {
        value: { type: String, default: "" },
        placeholder: { type: String, default: "Prompt metnini buraya yazın..." },
    },
    data() {
        return { editor: null, focused: false, internalUpdate: false };
    },
    methods: {
        getLabelForKey(key) {
            return getAttributeShortLabel(key) || key;
        },
        textToHtml(text) {
            if (!text) return "<p></p>";
            const self = this;
            return text.split("\n").map((line) => {
                if (line === "") return "<p><br></p>";
                const replaced = line.replace(/\{\{(\w+(?:\.\w+)*)\}\}/g, (_, key) => {
                    const label = self.getLabelForKey(key);
                    return '<copilot-attr name="' + key + '" label="' + label + '"></copilot-attr>';
                });
                return "<p>" + replaced + "</p>";
            }).join("");
        },
        htmlToText(html) {
            if (!html) return "";
            const el = document.createElement("div");
            el.innerHTML = html;
            const attrNodes = el.querySelectorAll("copilot-attr");
            for (let i = 0; i < attrNodes.length; i++) {
                const node = attrNodes[i];
                const textNode = document.createTextNode("{{" + (node.getAttribute("name") || "") + "}}");
                node.parentNode.replaceChild(textNode, node);
            }
            const paragraphs = el.querySelectorAll("p");
            if (paragraphs.length === 0) return el.textContent || "";
            const lines = [];
            for (let j = 0; j < paragraphs.length; j++) {
                const p = paragraphs[j];
                let txt = p.textContent || "";
                if (!txt && p.querySelector("br")) txt = "";
                lines.push(txt);
            }
            return lines.join("\n");
        },
        handleUpdate() {
            if (!this.editor || this.internalUpdate) return;
            const html = this.editor.getHTML();
            if (/\{\{\w+(?:\.\w+)*\}\}/.test(html)) {
                const self = this;
                const newHtml = html.replace(/\{\{(\w+(?:\.\w+)*)\}\}/g, (_, key) => {
                    return '<copilot-attr name="' + key + '" label="' + self.getLabelForKey(key) + '"></copilot-attr>';
                });
                self.internalUpdate = true;
                self.editor.chain().setContent(newHtml).run();
                self.$nextTick(() => { self.internalUpdate = false; });
                return;
            }
            const text = this.htmlToText(html);
            if (text !== this.value) this.$emit("input", text);
        },
        insertAttribute(key, label) {
            if (!this.editor) return;
            this.editor.chain().focus().insertContent(
                '<copilot-attr name="' + key + '" label="' + (label || this.getLabelForKey(key)) + '"></copilot-attr> '
            ).run();
        },
    },
    mounted() {
        const self = this;
        this.editor = new Editor({
            extensions: [
                StarterKit.configure({
                    bold: false, code: false, italic: false, heading: false,
                    codeBlock: false, blockquote: false, strike: false,
                    orderedList: false, bulletList: false, listItem: false,
                }),
                CoPilotAttrExtension,
                Placeholder.configure({ placeholder: self.placeholder, emptyEditorClass: "is-editor-empty" }),
            ],
            content: this.textToHtml(this.value),
            enablePasteRules: false,
            enableInputRules: false,
            onUpdate() { self.handleUpdate(); },
            onFocus() { self.focused = true; },
            onBlur() { self.focused = false; },
        });
    },
    watch: {
        value(newVal) {
            if (!this.editor) return;
            if (this.htmlToText(this.editor.getHTML()) !== newVal) {
                this.internalUpdate = true;
                this.editor.chain().setContent(this.textToHtml(newVal)).run();
                this.$nextTick(() => { this.internalUpdate = false; });
            }
        },
    },
    beforeDestroy() {
        if (this.editor) this.editor.destroy();
    },
};
</script>

<style lang="scss">
.cpe {
    border: 1px solid #ddd6fe;
    border-radius: 10px;
    background: #fff;
    transition: border-color 0.2s, box-shadow 0.2s;
    flex: 1;
    display: flex;
    flex-direction: column;

    &--focused {
        border-color: #8b5cf6;
        box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.12);
    }

    &__content .ProseMirror {
        padding: 14px 16px;
        min-height: 280px;
        overflow-y: auto;
        outline: none;
        font-size: 13px;
        line-height: 1.75;
        color: #1e1b4b;
        font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
        white-space: normal;
        word-wrap: break-word;
        overflow-wrap: break-word;

        p {
            margin: 0 0 0.4em 0;
            min-height: 1.2em;
        }

        &.is-editor-empty:first-child::before {
            content: attr(data-placeholder);
            float: left;
            color: #c4b5fd;
            pointer-events: none;
            height: 0;
        }
    }
}

.dark-mode .cpe {
    background: #1e1b4b;
    border-color: #4c1d95;
    &--focused { border-color: #7c3aed; box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.2); }
    &__content .ProseMirror { color: #ede9fe; }
}
</style>
